import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  LinearProgress,
} from "@mui/material";
import SpeedIcon from "@mui/icons-material/Speed";
import TimerIcon from "@mui/icons-material/Timer";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import LockIcon from "@mui/icons-material/Lock";
import BoltIcon from "@mui/icons-material/Bolt";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";

export default function PostUTMESpeedIntro() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [activeExam, setActiveExam] = useState(null);
  const [checkingActive, setCheckingActive] = useState(true);

  const [usage, setUsage] = useState(null);
  const [usageLoaded, setUsageLoaded] = useState(false);
  const [showWhyUpgrade, setShowWhyUpgrade] = useState(false);

  // ==========================================================
  // 🔎 CHECK ACTIVE EXAM
  // ==========================================================
  const checkActiveExam = useCallback(async () => {
    try {
      setCheckingActive(true);
      const res = await api.get("/exam/active");
      if (res.data?.hasActiveExam) {
        setActiveExam(res.data.exam);
      } else {
        setActiveExam(null);
      }
    } catch {
      setActiveExam(null);
    } finally {
      setCheckingActive(false);
    }
  }, []);

  // ==========================================================
  // 📊 LOAD USAGE
  // ==========================================================
  useEffect(() => {
    let mounted = true;

    const loadUsage = async () => {
      try {
        const res = await api.get("/exam/post-utme/usage");
        if (mounted) setUsage(res.data);
      } catch {
        if (mounted) setUsage(null);
      } finally {
        if (mounted) setUsageLoaded(true);
      }
    };

    loadUsage();
    checkActiveExam();

    return () => {
      mounted = false;
    };
  }, [checkActiveExam]);

  // ==========================================================
  // DERIVED FLAGS
  // ==========================================================
  const hasAnyActiveExam = !!activeExam?.id;
  const hasJambActive = activeExam?.exam_type === "JAMB";
  const isActivePostUTME = activeExam?.exam_type === "POST_UTME";
  const isActiveSubject = activeExam?.exam_type === "SUBJECT_PRACTICE";

  const remainingSeconds = Number.isInteger(activeExam?.remaining_seconds)
    ? activeExam.remaining_seconds
    : null;

  const remainingLabel =
    remainingSeconds == null
      ? null
      : `${Math.floor(remainingSeconds / 60)}:${String(
          remainingSeconds % 60
        ).padStart(2, "0")}`;

  const isEligibleUser = useMemo(() => {
    return (
      usageLoaded &&
      usage &&
      (usage.unlimited === true || usage.can_start === true)
    );
  }, [usageLoaded, usage]);

  const isBlocked = useMemo(() => {
    return (
      usageLoaded &&
      usage &&
      usage.can_start === false &&
      usage.unlimited !== true
    );
  }, [usageLoaded, usage]);

  const usageProgress = useMemo(() => {
    if (!usageLoaded || !usage || usage.unlimited) return 0;
    return Math.min(100, Math.round((usage.used / 3) * 100));
  }, [usageLoaded, usage]);

  // ==========================================================
  // ▶ START / RESUME (STRICT RULE)
  // ==========================================================
  const handleStart = async () => {
    if (loading || !usageLoaded || checkingActive) return;

    if (hasJambActive) return;

    if (hasAnyActiveExam && (isActivePostUTME || isActiveSubject)) {
      navigate(`/exam/session/${activeExam.id}`, { replace: true });
      return;
    }

    if (isBlocked) {
      navigate("/dashboard/my-subscriptions?plan=post-utme");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/exam/start", {
        exam_type: "POST_UTME",
      });

      if (res.data?.exam?.id) {
        navigate(`/exam/session/${res.data.exam.id}`, { replace: true });
        return;
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        await checkActiveExam();
        if (
          activeExam?.id &&
          (activeExam.exam_type === "POST_UTME" ||
            activeExam.exam_type === "SUBJECT_PRACTICE")
        ) {
          navigate(`/exam/session/${activeExam.id}`, { replace: true });
          return;
        }
      }
      window.alert(err?.response?.data?.message || "Unable to start exam");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <Box
      sx={{
        minHeight: "80vh",
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 3, sm: 5 },
        display: "flex",
        justifyContent: "center",
        overflowX: "hidden",
      }}
    >
      <Paper
        sx={{
          maxWidth: 880,
          width: "100%",
          borderRadius: { xs: 3, sm: 5 },
        }}
        elevation={6}
      >
        <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack spacing={3}>
            <Stack alignItems="center" textAlign="center" spacing={1.5}>
              <SpeedIcon sx={{ fontSize: { xs: 42, sm: 48 } }} />

              <Typography
                fontWeight={900}
                sx={{ fontSize: { xs: "1.6rem", sm: "2.1rem" } }}
              >
                General Post-UTME Speed Test
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                justifyContent="center"
              >
                <Chip icon={<TimerIcon />} label="20 Minutes" />
                <Chip icon={<TrackChangesIcon />} label="40 Questions" />
                <Chip icon={<BoltIcon />} label="High Pressure" />
              </Stack>
            </Stack>

            {hasAnyActiveExam && (
              <Alert severity="info">
                You have an active exam.
                <Stack
                  direction="row"
                  spacing={1}
                  mt={1}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip label={activeExam.exam_type} />
                  {remainingLabel && (
                    <Chip
                      color="warning"
                      label={`Time left: ${remainingLabel}`}
                    />
                  )}
                </Stack>

                {hasJambActive && (
                  <Typography variant="body2" mt={1}>
                    ⚠️ JAMB exams can only be resumed from the JAMB page.
                  </Typography>
                )}
              </Alert>
            )}

            {usageLoaded && !isEligibleUser && !isBlocked && (
              <Alert
                severity="warning"
                icon={<LockIcon />}
                action={
                  <Button size="small" onClick={() => setShowWhyUpgrade(true)}>
                    Why upgrade?
                  </Button>
                }
              >
                Subscribe to unlock Post-UTME Speed Test
              </Alert>
            )}

            {usageLoaded && isEligibleUser && usage && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight={700}>Your Access</Typography>
                {usage.unlimited ? (
                  <Alert severity="success">Unlimited access enabled</Alert>
                ) : (
                  <>
                    <Typography variant="body2">
                      Used: {usage.used} / 3
                    </Typography>
                    <LinearProgress
                      value={usageProgress}
                      variant="determinate"
                    />
                  </>
                )}
              </Paper>
            )}

            <Divider />

            <Box textAlign="center">
              <Button
                size="large"
                variant="contained"
                onClick={handleStart}
                disabled={loading || checkingActive || hasJambActive}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : hasJambActive ? (
                    <LockIcon />
                  ) : hasAnyActiveExam ? (
                    <ReplayIcon />
                  ) : isBlocked ? (
                    <LockIcon />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                sx={{
                  px: { xs: 3, sm: 5 },
                  py: 1.5,
                  fontWeight: 900,
                  borderRadius: 999,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {loading
                  ? "Loading…"
                  : hasJambActive
                  ? "JAMB Active"
                  : hasAnyActiveExam
                  ? "Resume Active Exam"
                  : isBlocked
                  ? "Upgrade Required"
                  : "Start Speed Test"}
              </Button>
            </Box>

            <Alert severity="warning" icon={<WarningAmberIcon />}>
              This is a simulated CBT speed test, not an official Post-UTME exam.
            </Alert>
          </Stack>
        </Box>
      </Paper>

      <Dialog
        open={showWhyUpgrade}
        onClose={() => setShowWhyUpgrade(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Why upgrade?</DialogTitle>
        <DialogContent dividers>
          <ul>
            <li>Unlimited speed tests</li>
            <li>CBT-style time pressure</li>
            <li>Better exam composure</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWhyUpgrade(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() =>
              navigate("/dashboard/my-subscriptions?plan=post-utme")
            }
          >
            Upgrade
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
